/**
 * useUserProfile — Fetches and updates the current user's profile data.
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface UserProfile {
  avatar: string | null;
  theme_color: string | null;
  display_name: string | null;
  location: string | null;
  interests: string[] | null;
}

export function useUserProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      // Try localStorage for guests
      const avatar = localStorage.getItem('user_avatar');
      const color = localStorage.getItem('user_color');
      setProfile({ avatar, theme_color: color, display_name: null, location: null, interests: null });
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('avatar, theme_color, display_name, location, interests')
        .eq('id', user.id)
        .single();

      if (error) {
        // Fallback to localStorage
        const avatar = localStorage.getItem('user_avatar');
        const color = localStorage.getItem('user_color');
        setProfile({ avatar, theme_color: color, display_name: null, location: null, interests: null });
      } else {
        const interests = Array.isArray(data.interests) ? data.interests as string[] : null;
        setProfile({ ...data, interests });
        // Sync to localStorage as backup
        if (data.avatar) localStorage.setItem('user_avatar', data.avatar);
        if (data.theme_color) localStorage.setItem('user_color', data.theme_color);
      }
      setLoading(false);
    };

    fetchProfile();
  }, [user]);

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) {
      // Guest mode - save to localStorage
      if (updates.avatar) localStorage.setItem('user_avatar', updates.avatar);
      if (updates.theme_color) localStorage.setItem('user_color', updates.theme_color);
      setProfile(prev => ({ ...prev, ...updates } as UserProfile));
      return { error: null };
    }

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (!error) {
      setProfile(prev => ({ ...prev, ...updates } as UserProfile));
      // Sync to localStorage
      if (updates.avatar) localStorage.setItem('user_avatar', updates.avatar);
      if (updates.theme_color) localStorage.setItem('user_color', updates.theme_color);
    }

    return { error };
  };

  return { profile, loading, updateProfile };
}
